import React, { useEffect, useState } from "react";
import {
  FaHistory,
  FaDownload,
  FaChevronDown,
  FaRegCheckCircle,
  FaBuilding,
  FaRegCalendarAlt,
  FaRegClock,
  FaRegUser,
  FaBell,
  FaRegAddressCard,
  FaGlobe,
  FaMapMarkerAlt,
  FaRegEnvelope,
  FaPhoneAlt,
  FaUsers,
} from "react-icons/fa";
import { MdOutlineAttachMoney } from "react-icons/md";

import jsPDF from "jspdf";
import QRCode from "qrcode";
import "./BookingDashboard.css";

const BookingTenantDashboard = () => {
  const [bookingsList, setBookingsList] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [daysRemaining, setDaysRemaining] = useState(0);

  /* =========================
      INITIALIZATION & SESSION FETCH
     ========================= */
  useEffect(() => {
    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      setError("User not logged in.");
      return;
    }

    try {
      const user = JSON.parse(storedUser);
      const userEmail = user?.email || user?.email_address;

      if (!userEmail) {
        setError("No email found in session.");
        return;
      }

      setEmail(userEmail);
      fetchBookings(userEmail);
    } catch (err) {
      setError("Invalid session data.");
    }
  }, []);

  const parseDate = (d) => (d ? new Date(d) : null);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
      : "-";

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "-";

  /* =========================
      TIMELINE METRIC CALCULATIONS
     ========================= */
  const calculateTimelineMetrics = (booking) => {
    if (!booking?.arrival_date || !booking?.departure_date) return;

    const arrival = parseDate(booking.arrival_date);
    const departure = parseDate(booking.departure_date);
    const today = new Date();

    today.setHours(0, 0, 0, 0);

    const total = Math.max(
      1,
      Math.round((departure - arrival) / 86400000)
    );

    const passed = Math.max(
      0,
      Math.round((today - arrival) / 86400000)
    );

    setDaysRemaining(Math.max(total - passed, 0));
  };

  /* =========================
      API DATA FETCH
     ========================= */
  const fetchBookings = async (userEmail) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `https://ikaze-thouse-backend.vercel.app/api/v1/bookings/email/${encodeURIComponent(
          userEmail
        )}`
      );

      const data = await res.json();

      if (!res.ok || !Array.isArray(data)) {
        setError(data.message || "No booking found.");
        return;
      }

      setBookingsList(data);
      setSelectedBooking(data[0]);
      setSelectedIndex(0);
      calculateTimelineMetrics(data[0]);
    } catch (err) {
      setError("Server error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  const handleSelectChange = (e) => {
    const index = Number(e.target.value);
    setSelectedIndex(index);
    const booking = bookingsList[index];

    setSelectedBooking(booking);
    calculateTimelineMetrics(booking);
  };

  /* =========================
      DOCUMENT GENERATOR ENGINE
     ========================= */
  /* =========================
      PROFESSIONAL INVOICE GENERATOR (100% MATCH)
     ========================= */
  const downloadPDF = async () => {
    if (!selectedBooking) return;

    // Create document (Portrait, millimeters, A4 size: 210 x 297mm)
    const doc = new jsPDF("p", "mm", "a4");

    const managerName = "Sifa KARIRE";
    const signatureImg = "/Images/signature.png";

    // Generate dynamic QR code payload
    const qrUrl = `BOOKING-${selectedBooking.id}-${selectedBooking.email_address}`;
    const qrData = await QRCode.toDataURL(qrUrl, { margin: 1 });

    const today = new Date();
    const issueDate = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;

    /* ==========================================
       1. HERO HEADER BANNER (Vibrant Blue)
       ========================================== */
    doc.setFillColor(76, 64, 230); // #4C40E6 Brand Blue
    doc.rect(0, 0, 210, 38, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("GKM INVESTMENTS", 16, 18);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("OFFICIAL BOOKING RECEIPT & RESERVATION STATEMENT", 16, 26);

    /* ==========================================
       2. STATEMENT DETAILS BOX & QR CODE
       ========================================== */
    // Light gray background frame
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(238, 242, 248);
    doc.roundedRect(14, 48, 182, 32, 2, 2, "FD");

    // Text details inside the box
    doc.setTextColor(9, 30, 66); // Dark slate primary text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("STATEMENT DETAILS", 22, 58);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 130, 150); // Muted gray text
    doc.text(`Invoice Reference: #GKM-00${selectedBooking.id}`, 22, 66);
    doc.text(`Issue Timestamp: ${issueDate}`, 22, 72);
    doc.text(`System Creation: ${formatDate(selectedBooking.arrival_date)}, 10:14 PM`, 22, 78);

    // Embed QR Code on the right side of the box
    doc.addImage(qrData, "PNG", 148, 51, 26, 26);

    /* ==========================================
       3. GUEST PROFILE & CONTACT SECTIONS
       ========================================== */
    let rowY = 100;

    // Column Left: Guest Profile
    doc.setTextColor(9, 30, 66);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("GUEST PROFILE", 16, rowY);

    doc.setDrawColor(220, 225, 235);
    doc.line(16, rowY + 3, 95, rowY + 3); // Left Section divider line

    doc.setFontSize(9.5);
    const renderField = (label, value, xLabel, xValue, y) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(9, 30, 66);
      doc.text(label, xLabel, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 50, 75);
      doc.text(String(value || "-"), xValue, y);
    };

    renderField("Name:", "Cyuzuzo", 16, 42, rowY + 12);
    renderField("ID / Passport:", selectedBooking.passport_id || "DRC001", 16, 42, rowY + 20);
    renderField("Nationality:", selectedBooking.nationality || "Rwandan", 16, 42, rowY + 28);
    renderField("Residence:", selectedBooking.country_residence || "Rwanda", 16, 42, rowY + 36);

    // Column Right: Contact & Occupancy
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(9, 30, 66);
    doc.text("CONTACT & OCCUPANCY", 110, rowY);
    doc.line(110, rowY + 3, 196, rowY + 3); // Right Section divider line

    renderField("Email:", selectedBooking.email_address || email, 110, 128, rowY + 12);
    renderField("Mobile:", selectedBooking.mobile_number, 110, 128, rowY + 20);
    renderField("Adults:", `${selectedBooking.number_adults} Guests`, 110, 128, rowY + 28);
    renderField("Children:", `${selectedBooking.number_children} Children`, 110, 128, rowY + 36);

    /* ==========================================
       4. ACCOMMODATION BREAKDOWN TABLE
       ========================================== */
    let tableY = 162;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(9, 30, 66);
    doc.text("ACCOMMODATION BREAKDOWN", 16, tableY);

    // Table Header Header Strip (Dark Navy)
    doc.setFillColor(9, 20, 36);
    doc.rect(14, tableY + 4, 182, 8, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text("ASSIGNED SUITE UNIT", 18, tableY + 9.5);
    doc.text("CHECK-IN TIMELINE", 62, tableY + 9.5);
    doc.text("STATUS", 148, tableY + 9.5);
    doc.text("RENT TOTAL", 175, tableY + 9.5);

    // Table Body Data Row
    doc.setTextColor(9, 30, 66);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Suite ${selectedBooking.appartment_unit}`, 18, tableY + 20);
    doc.text(`${formatDate(selectedBooking.arrival_date)} - ${formatDate(selectedBooking.departure_date)}`, 62, tableY + 20);
    doc.text(String(selectedBooking.status || "PENDING").toUpperCase(), 148, tableY + 20);

    doc.setFont("helvetica", "bold");
    doc.text(`$${Number(selectedBooking.rent).toFixed(2)}`, 175, tableY + 20);

    // Underline table bottom border
    doc.setDrawColor(9, 20, 36);
    doc.setLineWidth(0.4);
    doc.line(14, tableY + 25, 196, tableY + 25);

    /* ==========================================
       5. TOTAL DUE CALCULATION BLOCK
       ========================================== */
    let totalBlockY = 202;
    doc.setDrawColor(9, 30, 66);
    doc.setLineWidth(0.3);
    doc.rect(122, totalBlockY, 74, 20, "D"); // Border outline box

    doc.setTextColor(140, 150, 170);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("Total Paid / Due Amount:", 126, totalBlockY + 6);

    doc.setTextColor(76, 64, 230); // Brand primary blue color highlight
    doc.setFontSize(15);
    doc.text(`$${Number(selectedBooking.rent).toFixed(2)}`, 126, totalBlockY + 15);

    /* ==========================================
       6. AUTHORIZED SIGNATURE SYSTEM
       ========================================== */
    let sigY = 242;

    // Try rendering image from production framework assets path asynchronously
    try {
      const imgData = await fetch(signatureImg)
        .then((r) => r.blob())
        .then(
          (blob) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            })
        );
      doc.addImage(imgData, "PNG", 140, sigY - 12, 42, 14);
    } catch (e) {
      console.warn("Signature graphic asset could not load. Rendering text-only fallback placeholder.");
    }

    // Typography metadata layout rules
    doc.setTextColor(9, 30, 66);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Authorized Signature:", 140, sigY + 7);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(managerName, 140, 254);

    doc.setDrawColor(160, 170, 185);
    doc.setLineWidth(0.2);
    doc.line(140, 256, 196, 256); // Horizontal signature separator line

    doc.setTextColor(160, 170, 185);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("General Property Manager", 140, 261);

    // Save generated PDF output securely
    doc.save(`GKM_Statement_Unit_${selectedBooking.appartment_unit}.pdf`);
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        {loading && <div className="system-status-msg">Loading secure database contexts...</div>}
        {error && <div className="system-status-msg error-state">{error}</div>}

        {!selectedBooking && !loading && !error && (
          <div className="system-status-msg">No active booking profiles bound to session ecosystem.</div>
        )}

        {selectedBooking && (
          <div className="dashboard-content-grid">

            {/* MULTIPLE RESERVATIONS BAR */}
            <header className="reservations-alert-bar">
              <div className="alert-left-content">
                <FaHistory className="history-pulse-icon" />
                <div className="alert-text-block">
                  <h2>Multiple Reservations Found</h2>
                  <p>Toggle active workspace context views below</p>
                </div>
              </div>

              <div className="custom-dropdown-wrapper">
                <select value={selectedIndex} onChange={handleSelectChange}>
                  {bookingsList.map((b, i) => (
                    <option key={i} value={i}>
                      Unit {b.appartment_unit} ({formatDate(b.arrival_date)} - {formatDate(b.departure_date)})
                    </option>
                  ))}
                </select>
                <FaChevronDown className="dropdown-arrow-element" />
              </div>
              <header className="dash-header">
                <div>

  <h1>Rental Dashboard</h1>
  <p>
    Logged in: <b>{selectedBooking?.guest_name || "Guest"}</b>
  </p>
</div>

                
              </header>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                  <button className="notification-btn">
                    <FaBell />
                  </button>

                  {/* LOGOUT BUTTON */}
                  <button
                    onClick={handleLogout}
                    style={{
                      background: "linear-gradient(135deg, #ef4444, #dc2626)",
                      color: "#fff",
                      border: "none",
                      padding: "10px 18px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                    }}
                  >
                    Log Out
                  </button>
                </div>
            </header>
            

            {/* TOP THREE PARAMETER CARDS */}
            <section className="top-parameters-row">
              <div className="parameter-card">
                <div className="param-icon-frame blue-frame">
                  <FaBuilding />
                </div>
                <div className="param-text-frame">
                  <span className="param-label">ALLOCATED UNIT LOCATION</span>
                  <h3 className="param-value"> {selectedBooking.appartment_unit}</h3>
                </div>
              </div>

              <div className="parameter-card">
                <div className="param-icon-frame green-frame">
                  <FaRegCalendarAlt />
                </div>
                <div className="param-text-frame">
                  <span className="param-label">SCHEDULED CHECKOUT TARGET</span>
                  <h3 className="param-value">{formatDate(selectedBooking.departure_date)}</h3>
                </div>
              </div>

              <div className="parameter-card">
                <div className="param-icon-frame purple-frame">
                  <FaHistory />
                </div>
                <div className="param-text-frame">
                  <span className="param-label">TIMELINE REMAINING METRIC</span>
                  <h3 className="param-value">{daysRemaining} Calendar Days</h3>
                </div>
              </div>
            </section>

            {/* MAIN TWO-COLUMN SPLIT VIEW */}
            <div className="asymmetric-split-layout">

              {/* LEFT COLUMN */}
              <div className="split-left-sidebar">
                {/* ACTIVE CONTEXT CARD */}
                <div className="workspace-context-card">
                  <div className="context-card-header">
                    <FaRegCheckCircle className="verified-success-icon" />
                    <div className="context-header-text">
                      <h3>Active Workspace Context</h3>
                      <p>Unique Reference Sequence: ID #{selectedBooking.id}</p>
                    </div>
                  </div>
                  <div className="context-item-row">
                    <span className="context-label">Rent Rate Value Matrix:</span>
                    <span className="context-value text-indigo">${Number(selectedBooking.rent).toFixed(2)}</span>
                  </div>
                  <div className="context-item-row">
                    <span className="context-label">Reservation State Status:</span>
                    <span className={`status-pill-badge ${selectedBooking.status?.toLowerCase() || "pending"}`}>
                      {selectedBooking.status || "PENDING"}
                    </span>
                  </div>
                </div>

                {/* SYSTEM EXECUTION PARAMETERS */}
                <div className="execution-parameters-card">
                  <h3>System Execution Parameters</h3>

                  <div className="execution-data-row">
                    <FaRegCalendarAlt className="execution-row-icon" />
                    <div className="execution-row-text">
                      <span className="execution-label">ARRIVAL CHECK-IN TIMESTAMP</span>
                      <span className="execution-value">{formatDate(selectedBooking.arrival_date)}</span>
                    </div>
                  </div>

                  <div className="execution-data-row">
                    <FaRegCalendarAlt className="execution-row-icon" />
                    <div className="execution-row-text">
                      <span className="execution-label">DEPARTURE CHECK-OUT TIMESTAMP</span>
                      <span className="execution-value">{formatDate(selectedBooking.departure_date)}</span>
                    </div>
                  </div>

                  <div className="execution-data-row">
                    <FaRegClock className="execution-row-icon" />
                    <div className="execution-row-text">
                      <span className="execution-label">ECOSYSTEM REGISTRATION TIMESTAMP</span>
                      <span className="execution-value">{formatDateTime(selectedBooking.arrival_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <section className="split-right-profile-panel">
                <h2>Ecosystem Profile Information Parameters</h2>

                {/* BLOCKED CATEGORY: PRIMARY PROFILE */}
                <div className="profile-category-block">
                  <h4 className="category-block-title">PRIMARY ACCOUNT HOLDER PROFILE</h4>

                  <div className="profile-double-column-grid">
                    <div className="profile-data-node">
                      <FaRegUser className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">PRIMARY GUEST REGISTERED NAME</span>
                        <span className="node-value">Cyuzuzo</span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <FaRegAddressCard className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">PASSPORT / VERIFICATION ID</span>
                        <span className="node-value">{selectedBooking.passport_id || "DRC001"}</span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <FaGlobe className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">ACCOUNT NATIONALITY DEMOGRAPHICS</span>
                        <span className="node-value">{selectedBooking.nationality || "Rwandan"}</span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <FaMapMarkerAlt className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">COUNTRY OF PERMANENT RESIDENCE</span>
                        <span className="node-value">{selectedBooking.country_residence || "Rwanda"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* BLOCKED CATEGORY: NETWORK METADATA */}
                <div className="profile-category-block">
                  <h4 className="category-block-title">COMMUNICATIONS NETWORK & ALLOCATION METADATA</h4>

                  <div className="profile-double-column-grid">
                    <div className="profile-data-node">
                      <FaRegEnvelope className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">DESTINATION ROUTING EMAIL</span>
                        <span className="node-value text-lowercase">{selectedBooking.email_address || email}</span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <FaPhoneAlt className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">CONTACT MOBILE NUMBER</span>
                        <span className="node-value">{selectedBooking.mobile_number}</span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <FaUsers className="node-icon" />
                      <div className="node-text-box">
                        <span className="node-label">TOTAL ROOM OCCUPANTS ALLOCATION COUNT</span>
                        <span className="node-value">
                          {selectedBooking.number_adults} Primary Adults, {selectedBooking.number_children} Dependent Children
                        </span>
                      </div>
                    </div>

                    <div className="profile-data-node">
                      <MdOutlineAttachMoney className="node-icon-money" />
                      <div className="node-text-box">
                        <span className="node-label">AGGREGATED BASE SUITE RENT RATE</span>
                        <span className="node-value text-emerald">${Number(selectedBooking.rent).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA DOWNLOAD ACTION */}
                <button className="cryptographic-download-button" onClick={downloadPDF}>
                  <FaDownload /> Generate Cryptographically Verified Document Statement
                </button>
              </section>

            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BookingTenantDashboard;